import React, { useState, useEffect } from 'react';
import './Work.css';
import { getApiBaseUrl } from '../config/api';

export default function Work() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState(null);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/case-studies`);
      const data = await response.json();
      setCaseStudies(data.filter(cs => cs.status === 'published' || cs.featured));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching case studies:', err);
      setLoading(false);
    }
  };

  const featured = caseStudies.find(cs => cs.featured) || caseStudies[0];
  const others = caseStudies.filter(cs => cs._id !== featured?._id);

  if (loading) {
    return <section className="section shell" id="work"><p>Loading case studies...</p></section>;
  }

  return (
    <section className="section shell" id="work">
      <div className="cases-head">
        <div>
          <div className="section-label reveal">02 — Selected work</div>
          <h2 className="display cases-head__title reveal" style={{ marginTop: '32px' }}>
            Selected<br /><em style={{ color: 'rgba(244, 241, 234, 0.4)' }}>engagements.</em>
          </h2>
        </div>
        <p className="cases-head__sub reveal" style={{ color: 'rgba(244, 241, 234, 0.6)' }}>
          A small sample of platforms now operating in production at scale. Detailed case studies available under NDA.
        </p>
      </div>

      {featured && (
        <article className="case-feature reveal">
          <div className="case-feature__media" style={{ backgroundColor: featured.backgroundColor || '#ffffff', cursor: 'pointer' }} onClick={() => setSelectedStudy(featured)}>
            {featured.thumbnail ? (
              <img
                src={featured.thumbnail}
                alt={featured.client}
                style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '24px' }}
              />
            ) : (
              <div className="placeholder-stripes" style={{ position: 'absolute', inset: 0 }}></div>
            )}
          </div>

          <div className="case-feature__body">
            <div className="case-feature__client">{featured.client}</div>
            <h3 className="case-feature__title">{featured.title}</h3>
            <p className="case-feature__desc">
              {featured.description}
            </p>
            <button
              className="case-feature__link"
              onClick={() => setSelectedStudy(featured)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Read the case study <span>↗</span>
            </button>
          </div>
        </article>
      )}

      <div className="case-grid">
        {others.map(cs => (
          <article key={cs._id} className="case reveal" onClick={() => setSelectedStudy(cs)} style={{ cursor: 'pointer' }}>
            <div className="case__media" style={{ backgroundColor: cs.backgroundColor || '#ffffff' }}>
              {cs.thumbnail ? (
                <img
                  src={cs.thumbnail}
                  alt={cs.client}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '16px' }}
                />
              ) : (
                <>
                  <div className="placeholder-stripes" style={{ position: 'absolute', inset: 0 }}></div>
                  <span className="placeholder-label">[ {cs.client} ]</span>
                </>
              )}
            </div>
            <div className="case__head">
              <span className="case__client">{cs.client}</span>
              <span className="case__year">{cs.year}</span>
            </div>
            {cs.subtitle && (
              <p className="case__subtitle">{cs.subtitle}</p>
            )}
            {cs.tags && cs.tags.length > 0 && (
              <div className="case__tags">
                {cs.tags.slice(0, 3).map((tag, idx) => (
                  <span key={idx} className="case__tag">{tag}</span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>

      {selectedStudy && (
        <div className="modal-overlay" onClick={() => setSelectedStudy(null)}>
          <div className="case-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedStudy(null)}>✕</button>
            <div className="case-modal__content">
              <div className="case-modal__header">
                <span className="case-modal__client">{selectedStudy.client}</span>
                <h2 className="case-modal__title">{selectedStudy.title}</h2>
                {selectedStudy.description && (
                  <p className="case-modal__subtitle">{selectedStudy.description}</p>
                )}
              </div>

              <div className="case-modal__grid">
                {selectedStudy.year && (
                  <div className="case-modal__field">
                    <span className="case-modal__label">Year</span>
                    <div className="case-modal__value">{selectedStudy.year}</div>
                  </div>
                )}
                {selectedStudy.industry && (
                  <div className="case-modal__field">
                    <span className="case-modal__label">Industry</span>
                    <div className="case-modal__value">{selectedStudy.industry}</div>
                  </div>
                )}
                {selectedStudy.scope && (
                  <div className="case-modal__field">
                    <span className="case-modal__label">Scope</span>
                    <div className="case-modal__value">{selectedStudy.scope}</div>
                  </div>
                )}
                {selectedStudy.timeline && (
                  <div className="case-modal__field">
                    <span className="case-modal__label">Timeline</span>
                    <div className="case-modal__value">{selectedStudy.timeline}</div>
                  </div>
                )}
              </div>

              {selectedStudy.fullDescription && (
                <div className="case-modal__body">
                  <p>{selectedStudy.fullDescription}</p>
                </div>
              )}

              {selectedStudy.tags && selectedStudy.tags.length > 0 && (
                <div className="case-modal__tags">
                  {selectedStudy.tags.map((tag, idx) => (
                    <span key={idx} className="case-modal__tag">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
